import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';

import { DocumentFinderService } from '~/services/document-finder.service';
import {
    DocumentFinderApiService, PblCheckResponse, PblCheckResult, PblGenerateJob
} from '~/services/document-finder-api.service';

/** Most documents one generation run accepts (backend limit). */
const MAX_GENERATE_DOCS = 30;
const POLL_MS = 2000;

export type PblKind = 'FIT' | 'GAP' | 'Fit & Gap';

export interface PblItem {
    id: string;
    kind: PblKind;
    priority: string;
    tag: string;
    story: string;
    context: string;
    chips: string[];
    reasoning?: string;
    /** Initial status used when the backlog is first shown. */
    status: 'pending' | 'approved' | 'rejected';

    /** Fields from spec-pbl.md. Matched items only report the existing PBL, so most of these stay empty for them. */
    title?: string;
    /** Set when the requirement already exists in the SharePoint backlog (match_status = matched). */
    match?: { pblId: string; overlap: number };
    l3?: string;
    subTeam?: 'AM' | 'MM' | 'FIN' | 'HCVC';
    /** WRICEF type when kind is GAP. */
    extension?: string;
    acceptance?: string[];
    value?: string;
    dependencies?: string[];
    assumptions?: string[];
    openQuestions?: string[];
    sources?: string[];
    /** Generated PBLs only: Definition of Done points the reviewer should look at. */
    checks?: string[];
}

/**
 * Sample backlog for the PBLs step, written to spec-pbl.md from the L1 Production Execution documents
 * (EN03437 blend optimisation spec, process manuals, SDR list). Matched items point at real approved PBLs in the
 * SharePoint FitGap Interim Product Backlog. Replace with the API response once the backlog endpoint is available.
 */
export const PBL_MOCK: PblItem[] = [
    // Net new: no approved SharePoint PBL covers these.
    { id: 'PE-L1-01', kind: 'GAP', extension: 'Enhancement', priority: 'High', tag: '', l3: 'LSC-210-030', subTeam: 'HCVC', status: 'pending',
      title: 'Predict and optimise blend viscosity and treat rate for lubricant blend orders',
      story: 'As a lubricants lab analyst, I want the system to predict and then optimise treat rate and viscosity for a blend order, so that blends are right first time and less viscosity index improver is used.',
      context: 'Lab analysts need to predict a blend\'s initial viscosity, density and treat rates from the actual component batches and then optimise base-oil quantities to reach the target viscosity. Today this runs in OCS-FBS in the US, which is being retired, and in Excel outside the US.',
      chips: ['Process Order', 'BOM', 'Batch', 'Inspection Plan'],
      acceptance: [
          'Given a blend order with a batch assigned to every component, When I run Predict, Then initial viscosity, density and treat rates are calculated from the batch characteristics and the BOM mass/volume percentages.',
          'Given a blend order is opened, When the screen loads, Then the target values default to the typical values from the QM inspection plan of the finished product.',
          'Given predicted values exist, When I run Optimise Treat Rate and then Optimise Viscosity, Then optimised base-oil quantities are shown next to the typical, target and initial values.',
          'Given I try to optimise viscosity before treat rate, When I press Optimise Viscosity, Then the system stops me and tells me to optimise treat rate first.',
          'Given an optimised result moves a component outside its BOM min/max treat rate, When the result is shown, Then a message names the component and the limit reached.',
          'Given I change a target value after optimising, When the change is saved, Then the optimised values are cleared and I must re-run both optimisations.'
      ],
      value: 'The spec states blends can be 50,000+ gallons worth around $100,000 each and the global network will optimise about 100 orders per day; an off-test blend risks the blend value plus rework, disposal and lost blend capacity. Also replaces the retiring OCS-FBS module and hundreds of non-US Excel sheets.',
      dependencies: [
          'PE-L1-02 blending fields on BOM items',
          'QM inspection plans with viscosity, density and metal specs',
          'Batch management active in the plant',
          'QM go-live at least four weeks before blend optimisation (per spec)'
      ],
      assumptions: [
          'The ASTM-based calculation logic in EN03437 is reused',
          'Only real batches (not generic batches) are used in the calculation'
      ],
      openQuestions: [
          'Will the S/4 solution be the ZBLOP ALV screen or the Fiori worklist shown in the overview deck?',
          'Are both methods in scope: US K-factor optimisation to finished-product viscosity and non-US optimisation to total base-oil viscosity?',
          'ECC uses production order type ZCB1. Will S/4 use production orders or process orders for blends?',
          'Which plants after the Singapore pilot, and in what order?'
      ],
      sources: [
          'SPS_PP_EN03437 §2.1.1 Business information',
          'SPS_PP_EN03437 §4.5.1 Functional details',
          'Blend Optimization - Overview.pptx',
          'PO-BD-04_ILT_Blend Optimization.pptx'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Enhancement).' },
    { id: 'PE-L1-02', kind: 'GAP', extension: 'Enhancement', priority: 'High', tag: '', l3: 'LSC-630-010', subTeam: 'HCVC', status: 'pending',
      title: 'Maintain blending data on BOM items: K-factor, blend type and min/max treat rate',
      story: 'As a lubricants formulation master data specialist, I want to maintain K-factor, blend type and treat-rate limits on each BOM item, so that the blend optimiser has the data it needs and keeps results within approved limits.',
      context: 'The optimisation needs extra data on each BOM item: K-factor, blend item type, mass and volume treat rate, and minimum/maximum treat rate. In ECC these were custom BOM fields (EN03385); the min/max limits come from Technology (GPIMS/PLM).',
      chips: ['BOM', 'Material'],
      acceptance: [
          'Given a lubricant BOM item, When I edit it, Then I can enter K-factor, blend type, mass treat rate, volume treat rate, minimum treat rate and maximum treat rate.',
          'Given the blend type field, When I open the value list, Then it offers U (Base Oil), T (Fixed Treat Rate), A (Metal Content), P (Production Oils), K (Variable Content), H (Heel Adjustment), B (Designate Blend) and O (Production Oil).',
          'Given I enter a maximum treat rate lower than the minimum, When I save, Then the system shows an error and does not save.',
          'Given a blend order whose BOM item has no blend type, When the order is opened in the optimiser, Then a message names the item with missing data.'
      ],
      value: 'Prerequisite for PE-L1-01: without these fields the optimisation cannot run, so none of the blend optimisation savings can be realised.',
      dependencies: [
          'PE-L1-01',
          'GPIMS/PLM as source of treat-rate limits',
          'E-BOM to M-BOM link from the PSI project'
      ],
      assumptions: [
          'Master data can be set up globally before plant go-live (per spec §4.12)'
      ],
      openQuestions: [
          'Will the treat-rate limits be loaded from GPIMS/PLM by interface or maintained by hand?',
          'Is the header field \'Viscosity Mode for Blend Optimization\' (ZZ_VISMODE) still needed?'
      ],
      sources: [
          'SPS_PP_EN03437 §4.1 BOM relevant custom fields',
          'SPS_PP_EN03437 §4.12 Deployment (EN03385)',
          'PO-BD-04_ILT_Blend Optimization.pptx BOM master data'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Enhancement).' },
    { id: 'PE-L1-07', kind: 'FIT', priority: 'High', tag: '', l3: 'CVX_QMP_1MR', subTeam: 'HCVC', status: 'pending',
      title: 'Create inspection lot on process order release and release stock on usage decision',
      story: 'As a quality technician, I want an inspection lot created on process order release and stock released automatically after my usage decision, so that no batch is used before it passes testing.',
      context: 'When a process order is released, an inspection lot is created and the produced stock goes to quality inspection. After testing and the usage decision, results go to the batch and stock moves to unrestricted automatically.',
      chips: ['Inspection Lot', 'Process Order', 'Batch', 'Usage Decision'],
      acceptance: [
          'Given a QM-active material, When its process order is released, Then an inspection lot is created for the order.',
          'Given the goods receipt is posted, When I check stock, Then the batch is in quality inspection status.',
          'Given results are recorded and an accept usage decision is made, When I display the batch, Then its characteristics show the inspection results.',
          'Given an accept usage decision, When it is saved, Then the stock moves from quality inspection to unrestricted without a manual posting.'
      ],
      value: 'Prevents untested product from being shipped or used, and removes a manual stock transfer per batch.',
      dependencies: [
          'Batch management (CVX_BLF)',
          'BJ8 Make-to-Stock process manufacturing',
          'Inspection plans per material'
      ],
      assumptions: [
          'Tank is physically sealed before sampling, as in the process flow'
      ],
      openQuestions: [
          'What should happen to the stock on a reject usage decision?',
          'Which inspection type will be used for process order lots?'
      ],
      sources: [
          'CVX_QMP_1MR Quality Management in Process Manufacturing.pdf'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 covers it, so it is a Fit. Closest existing: PBL14083 (69%), PBL12386 (56%); the reviewer should confirm.' },
    { id: 'PE-L1-09', kind: 'GAP', extension: 'Interface', priority: 'Medium', tag: '', l3: 'LSC-630-040', subTeam: 'HCVC', status: 'pending',
      title: 'Show in-process (WIP) sample results from PI alongside the process order',
      story: 'As a quality technician, I want to see in-process sample results recorded in PI for a process order, so that I can spot product going off-spec before the order is finished.',
      context: 'In the BJ8 process-order flow (CREG), samples taken during work in progress are tested and the results recorded in the PI system. The flow then shows displaying and reporting these in-process results.',
      chips: ['Process Order', 'Inspection Lot', 'Batch'],
      acceptance: [
          'Given in-process samples are recorded in PI for an order, When I open the in-process quality report for that order, Then each sample result is listed with its time.',
          'Given a result is outside specification, When the report is shown, Then that result is highlighted.'
      ],
      value: 'Lets the lab correct a blend during production instead of after the batch is finished.',
      dependencies: [
          'PI system',
          'PE-L1-07'
      ],
      openQuestions: [
          'Should results be shown in S/4, or is a PI report enough? If PI only, this may be out of SAP scope.',
          'Should PI results create S/4 inspection results, or display only?'
      ],
      sources: [
          'CVX_QMP_1MR Quality Management in Process Manufacturing.pdf (WIP sample branch)'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Interface).' },
    { id: 'PE-L1-19', kind: 'FIT', priority: 'Medium', tag: '', l3: 'M-040-080', subTeam: 'HCVC', status: 'pending',
      title: 'Post refinery production and consumption in S/4 where no Sigmafine exists',
      story: 'As a refinery hydrocarbon accountant, I want to post refinery production and consumption in S/4, so that the volume balance and costs are recorded for sites without Sigmafine.',
      context: 'South Africa calculates production and consumption (volume only) in SAP and posts goods receipts to production orders and goods issues to cost centres valued at moving average price.',
      chips: ['Production Order', 'Material', 'Cost Center'],
      acceptance: [
          'Given calculated production volumes, When I post them, Then goods receipts are posted against the refinery production order.',
          'Given calculated consumption volumes, When I post them, Then goods issues are posted to the cost centre at moving average price.'
      ],
      value: 'Keeps refinery inventory and cost correct each month for sites that calculate in SAP.',
      dependencies: [
          'DC-010-060 Post Accounting Documents'
      ],
      openQuestions: [
          'Is South Africa in scope for the S/4 release?'
      ],
      sources: [
          'M-040 Process Manual: overview (South Africa)'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 covers it, so it is a Fit.' },
    { id: 'PE-L1-21', kind: 'GAP', extension: 'Interface', priority: 'Medium', tag: '', l3: 'M-040', subTeam: 'AM', status: 'pending',
      title: 'Send material, vendor, purchase order and invoice data to Maximo Refining',
      story: 'As a refinery maintenance planner, I want material, vendor, PO and invoice data from S/4 in Maximo, so that maintenance work uses current supply data.',
      context: 'The SDR list includes interfaces from SAP to Maximo Refining for material master (IT02776), purchase orders (IT02777), vendors (IT03297), invoices (IT03299) and CO postings (IT02848).',
      chips: ['Material', 'Vendor', 'Purchase Order', 'Invoice'],
      acceptance: [
          'Given a material, vendor or PO is created or changed in S/4, When the interface runs, Then Maximo shows the update.',
          'Given an invoice is posted for a Maximo PO, When the interface runs, Then the invoice status is shown in Maximo.'
      ],
      value: 'Not stated in the source.',
      dependencies: [
          'Maximo Refining'
      ],
      openQuestions: [
          'Does Maximo Refining stay after S/4 go-live, or is it replaced by S/4 asset management?',
          'IT02777 target is listed as MCI Track (Oak Point), not Maximo. Which is correct?'
      ],
      sources: [
          'Production Execution SDR List.xlsx: IT02776, IT02777, IT03297, IT03299, IT02848'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Interface).' },
    { id: 'PE-L1-23', kind: 'GAP', extension: 'Enhancement', priority: 'High', tag: '', l3: 'DS-020-030', subTeam: 'FIN', status: 'pending',
      title: 'Calculate daily reference cost for light products from market quotes',
      story: 'As a product cost analyst, I want light product standard costs updated daily from market quotes, so that CO-PA shows product cost as of the delivery date.',
      context: 'A custom nightly job loads Platts, OPIS and Lundberg quotes, applies F&A pricing formulas and updates the standard cost of light products. EN00184, EN01629 and EN02589 support this.',
      chips: ['Material', 'Cost Estimate'],
      acceptance: [
          'Given new daily quotes, When the nightly job runs, Then each light product\'s standard cost is updated using its F&A formula.',
          'Given a quote is missing for a product, When the job runs, Then that product keeps its prior cost and is listed in an error report.',
          'Given a delivery and invoice on different dates, When billing posts to CO-PA, Then the cost difference between the two dates is calculated.'
      ],
      value: 'Light product margins in CO-PA depend on this daily cost.',
      dependencies: [
          'Platts, OPIS and Lundberg quote feeds',
          'F&A formula repository (Z-070-160)'
      ],
      openQuestions: [
          'Will PC2 and PC8 time-zone run times still be needed in S/4?'
      ],
      sources: [
          'DS-020 Process Manual: DS-020-030 Light Products',
          'Production Execution SDR List.xlsx: EN00184, EN01629, EN02589'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Enhancement). Closest existing: PBL12802 (44%), PBL13202 (43%); the reviewer should confirm.' },
    { id: 'PE-L1-25', kind: 'GAP', extension: 'Report', priority: 'Medium', tag: '', l3: 'DS-020-070', subTeam: 'FIN', status: 'pending',
      title: 'Review standard versus actual product cost to decide on revaluation',
      story: 'As a product cost analyst, I want to compare standard and actual product cost by location, so that I can decide when a rate needs to change.',
      context: 'The business compares standard rates with actual cost (CO-PA, cost centre, plant volume, sales data) to decide if rates need changing. EN02590 (Cost Revaluation Analysis Tool) and EN02587 (Product Costing Evaluation Program) support this.',
      chips: ['Material', 'Cost Estimate', 'CO-PA'],
      acceptance: [
          'Given a period and plant, When I run the analysis, Then standard and actual cost per material are shown with the difference.',
          'Given moving-average materials, When I run the analysis, Then materials whose price moved more than a set tolerance are flagged.'
      ],
      value: 'Keeps standard rates aligned with actual cost over time, as DS-020-070 requires.',
      dependencies: [
          'CO-PA',
          'BW'
      ],
      openQuestions: [
          'What tolerance triggers a rate change?',
          'Can standard S/4 analytics replace EN02590 and EN02587?'
      ],
      sources: [
          'DS-020 Process Manual: DS-020-070',
          'Production Execution SDR List.xlsx: EN02590, EN02587'
      ],
      reasoning: 'Net new. No approved SharePoint PBL covers this. Standard S/4 does not cover it, so it is a Gap (Report).' },
    // Matched: already approved in the SharePoint backlog. Reported, not redrafted.
    { id: 'PE-L1-10', kind: 'FIT', priority: 'Medium', tag: '', l3: 'CVX_QMP_1MR', subTeam: 'HCVC', status: 'pending',
      match: { pblId: 'PBL09716', overlap: 90 },
      title: 'Print certificate of analysis for a batch on demand',
      story: 'As a quality technician, I want to print a certificate of analysis for an approved batch, so that I can give customers proof the product meets specification.',
      context: 'After the usage decision, the quality technician prints a certificate of analysis (or acceptance) for the batch when needed.',
      chips: ['Batch', 'Inspection Lot', 'Certificate'],
      acceptance: [
          'Given a batch with an accepted usage decision, When I request a certificate of analysis, Then a certificate is produced showing the batch\'s results and specification limits.',
          'Given a batch without a usage decision, When I request a certificate, Then the system does not produce it and tells me why.'
      ],
      value: 'Customers receive test evidence for each batch, as required by the process flow.',
      dependencies: [
          'PE-L1-07'
      ],
      openQuestions: [
          'Is a Chevron-specific certificate layout needed (would make this a Form gap)?'
      ],
      sources: [
          'CVX_QMP_1MR Quality Management in Process Manufacturing.pdf (Print Certificate of Analysis)'
      ],
      reasoning: 'Matched. Approved SharePoint PBL PBL09716 already covers this (about 90% overlap), so it is reported, not redrafted. Found in CVX_QMP_1MR Quality Management in Process Manufacturing.pdf (Print Certificate of Analysis).' },
    { id: 'PE-L1-11', kind: 'GAP', extension: 'Enhancement', priority: 'Medium', tag: '', l3: 'LSC-310-055', subTeam: 'HCVC', status: 'pending',
      match: { pblId: 'PBL12681', overlap: 70 },
      title: 'Send certificate of analysis to the customer automatically after goods issue',
      story: 'As a customer service representative, I want the certificate of analysis sent to the customer automatically when goods issue is posted, so that customers get it without a manual request.',
      context: 'Legacy extension EN03534 sends the certificate of analysis automatically once goods issue is posted for a delivery.',
      chips: ['Delivery', 'Certificate', 'Customer'],
      acceptance: [
          'Given a delivery for a customer who needs certificates, When goods issue is posted, Then a certificate of analysis for each delivered batch is sent to the customer\'s contact.',
          'Given sending fails, When the output is processed, Then the failure is logged and can be re-sent.'
      ],
      value: 'Removes a manual email per delivery for customers who need certificates.',
      dependencies: [
          'PE-L1-10',
          'Customer output settings'
      ],
      openQuestions: [
          'Can standard S/4 output management do this without custom code?',
          'Which customers need automatic certificates, and by which channel?'
      ],
      sources: [
          'Production Execution SDR List.xlsx: EN03534 Auto Send COA after PGI'
      ],
      reasoning: 'Matched. Approved SharePoint PBL PBL12681 already covers this (about 70% overlap), so it is reported, not redrafted. Found in Production Execution SDR List.xlsx: EN03534 Auto Send COA after PGI.' },
    { id: 'PE-L1-18', kind: 'GAP', extension: 'Interface', priority: 'High', tag: '', l3: 'M-040-090', subTeam: 'HCVC', status: 'pending',
      match: { pblId: 'PBL11023', overlap: 90 },
      title: 'Receive refinery volume balance from Sigmafine into S/4',
      story: 'As a refinery hydrocarbon accountant, I want Sigmafine volumes posted to S/4 on the agreed schedule, so that weekly supply planning and month-end use the same refinery balance.',
      context: 'US refineries calculate the stock balance in Sigmafine. Inventories are sent to SAP weekly (by Thursday noon PST) and production, consumption and gain/loss monthly on workday 4.',
      chips: ['Material', 'Plant', 'Goods Movement'],
      acceptance: [
          'Given the weekly Sigmafine inventory file, When it is received by Thursday noon PST, Then inventories are updated in S/4.',
          'Given the monthly file on workday 4, When it is processed, Then production, consumption and gain/loss are posted.',
          'Given a record fails, When the interface runs, Then the error is logged and can be reprocessed.'
      ],
      value: 'Data feeds weekly supply planning, month-end financials and government reporting per M-040.',
      dependencies: [
          'Sigmafine',
          'SAP Silo Management',
          'BW government reporting'
      ],
      openQuestions: [
          'Is Silo Management kept in S/4?'
      ],
      sources: [
          'M-040 Process Manual: M-040-090 US interface timing',
          'M-040 Process Manual: overview'
      ],
      reasoning: 'Matched. Approved SharePoint PBL PBL11023 already covers this (about 90% overlap), so it is reported, not redrafted. Found in M-040 Process Manual: M-040-090 US interface timing.' },
    { id: 'PE-L1-29', kind: 'GAP', extension: 'Interface', priority: 'Medium', tag: '', l3: 'LSC-310-055', subTeam: 'HCVC', status: 'pending',
      match: { pblId: 'PBL10632', overlap: 75 },
      title: 'Print bill of lading and packing slip triggered from PCS',
      story: 'As a shipping clerk, I want the bill of lading and packing slip printed when PCS confirms the load, so that the driver leaves with the right documents.',
      context: 'Interface IT08040 has PCS trigger printing of the bill of lading and packing slip in SAP. LSC-310 D1.11 requires shipping documents for customers, carriers and government.',
      chips: ['Delivery', 'Shipment', 'Output'],
      acceptance: [
          'Given PCS sends a print request for a delivery, When it is received, Then the bill of lading and packing slip are printed at the site printer.',
          'Given the print fails, When it is processed, Then the error is logged and can be reprinted.'
      ],
      value: 'Not stated in the source.',
      dependencies: [
          'SAP PCS (SCM)'
      ],
      openQuestions: [
          'Is PCS kept after go-live?'
      ],
      sources: [
          'Production Execution SDR List.xlsx: IT08040 SAP PCS to PC2 - Print BOL / Pack Slip',
          'LSC-310 Process Manual: D1.11'
      ],
      reasoning: 'Matched. Approved SharePoint PBL PBL10632 already covers this (about 75% overlap), so it is reported, not redrafted. Found in Production Execution SDR List.xlsx: IT08040 SAP PCS to PC2 - Print BOL / Pack Slip.' },
    { id: 'PE-L1-30', kind: 'GAP', extension: 'Interface', priority: 'Medium', tag: '', l3: 'LSC-310-050', subTeam: 'HCVC', status: 'pending',
      match: { pblId: 'PBL12679', overlap: 70 },
      title: 'Include pallet data in the advance ship notice (EDI 856)',
      story: 'As a customer service representative, I want pallet details included in the advance ship notice, so that customers can receive shipments by pallet.',
      context: 'Interface IT08063 sends pallet table data from PCS to SAP for the EDI 856 advance ship notice.',
      chips: ['Delivery', 'Handling Unit', 'IDoc'],
      acceptance: [
          'Given a packed delivery with pallets in PCS, When the ASN is sent, Then each pallet and its contents are listed in the EDI 856.',
          'Given pallet data is missing, When the ASN is created, Then it is held and an error is logged.'
      ],
      value: 'Not stated in the source.',
      dependencies: [
          'SAP PCS (SCM)',
          'EDI'
      ],
      openQuestions: [
          'Which customers require pallet-level ASNs?'
      ],
      sources: [
          'Production Execution SDR List.xlsx: IT08063 PCS to PC2 - Interface for pallet table in EDI 856'
      ],
      reasoning: 'Matched. Approved SharePoint PBL PBL12679 already covers this (about 70% overlap), so it is reported, not redrafted. Found in Production Execution SDR List.xlsx: IT08063 PCS to PC2 - Interface for pallet table in EDI 856.' }
];

@Component({
    selector: 'df-pbl-step',
    templateUrl: './df-pbl-step.component.html',
    styleUrls: ['./df-pbl-step.component.scss'],
    standalone: false
})
export class DfPblStepComponent implements OnInit, OnDestroy {
    /** 'examples' shows the sample backlog (PBL_MOCK); 'generated' shows drafts from the approved documents. */
    public mode: 'examples' | 'generated' = 'examples';
    public generated: PblItem[] = [];
    /** Current or last generation job. */
    public job: PblGenerateJob | null = null;
    public readonly kinds: PblKind[] = ['FIT', 'GAP', 'Fit & Gap'];
    public selectedReasoningId: string | null = null;
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** SharePoint duplicate check state. Warnings only: never blocks approve/reject or continue. */
    public dupState: 'idle' | 'checking' | 'done' | 'error' = 'idle';
    public dupCheck: PblCheckResponse | null = null;
    private dupById: Record<string, PblCheckResult> = {};
    private exampleDup: PblCheckResponse | null = null;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollErrors = 0;
    private destroyed = false;

    constructor(public df: DocumentFinderService, private api: DocumentFinderApiService) {}

    public get items(): PblItem[] {
        return this.mode === 'generated' ? this.generated : PBL_MOCK;
    }

    public get matchedCount(): number {
        return this.items.filter(i => i.match).length;
    }

    public get generating(): boolean {
        return this.job?.status === 'running';
    }

    /** Documents approved in the review step: the only input to the generator. */
    public get approvedCount(): number {
        return this.df.included().length;
    }

    /** Why generation is not possible right now, or '' when it is. */
    public get generateBlocker(): string {
        if (!this.api.enabled) { return 'The backend is not connected.'; }
        if (!this.api.lastRunId) { return 'Run a search first.'; }
        if (!this.approvedCount) { return 'Approve at least one document in the review step first.'; }
        return '';
    }

    ngOnInit(): void {
        const last = this.api.lastPblJob;
        if (last && last.runId === this.api.lastRunId) {
            this.job = last;
            if (last.status === 'running') {
                this.poll();
            } else if (last.status === 'done' && last.items.length) {
                this.showGenerated(last, false);
                return;
            }
        }
        if (!this.df.pbl) {
            this.seed();
        }
        this.checkDuplicates();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.stopPolling();
    }

    /** Drafts PBLs from the documents approved in the review step (step 5). */
    public async generate(): Promise<void> {
        if (this.generating || this.generateBlocker) { return; }
        const ids = this.df.included().map(d => d.id);
        if (ids.length > MAX_GENERATE_DOCS) {
            this.df.toast(`Only the first ${MAX_GENERATE_DOCS} of ${ids.length} approved documents are used.`);
        }
        try {
            this.job = await this.api.startPblGeneration(ids.slice(0, MAX_GENERATE_DOCS));
            this.api.lastPblJob = this.job;
            this.pollErrors = 0;
            this.poll();
        } catch (err: any) {
            this.df.toast('Could not start generating: ' + (err?.error?.detail || err?.message || 'unknown error'));
        }
    }

    /** Switches back to the sample backlog. */
    public showExamples(): void {
        this.mode = 'examples';
        this.seed();
        if (this.exampleDup) {
            this.applyDup(this.exampleDup);
        } else {
            this.checkDuplicates();
        }
    }

    /** Switches to the last generated drafts (keeps their approve/reject decisions). */
    public showLastGenerated(): void {
        if (this.job?.status === 'done' && this.job.items.length) {
            this.showGenerated(this.job, false);
        }
    }

    private poll(): void {
        this.stopPolling();
        this.pollTimer = setTimeout(async () => {
            if (!this.job || this.destroyed) { return; }
            try {
                this.job = await this.api.getPblJob(this.job.jobId);
                this.api.lastPblJob = this.job;
                this.pollErrors = 0;
            } catch (err: any) {
                if (err?.status === 404 || ++this.pollErrors >= 5) {
                    this.job = { ...this.job, status: 'failed', message: 'Lost track of the generation. Try again.' };
                    this.api.lastPblJob = this.job;
                    return;
                }
            }
            if (this.destroyed) { return; }
            if (this.job.status === 'running') {
                this.poll();
            } else if (this.job.status === 'done' && this.job.items.length) {
                this.showGenerated(this.job, true);
                this.df.toast(this.job.message || 'Backlog generated');
            } else {
                this.df.toast(this.job.message || 'No PBLs were generated.');
            }
        }, POLL_MS);
    }

    private stopPolling(): void {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private showGenerated(job: PblGenerateJob, fresh: boolean): void {
        if (this.mode === 'examples' && this.dupCheck) {
            this.exampleDup = this.dupCheck;
        }
        this.mode = 'generated';
        this.generated = job.items.map(i => ({ ...i }));
        const statuses = { ...(this.df.pbl ?? {}) };
        this.generated.forEach(i => {
            if (fresh || !statuses[i.id]) { statuses[i.id] = 'pending'; }
            if (fresh) { delete this.df.pblKinds[i.id]; }
        });
        this.df.pbl = statuses;
        this.selectedReasoningId = null;
        if (job.duplicateCheck) {
            this.applyDup(job.duplicateCheck);
        } else {
            this.checkDuplicates();
        }
    }

    private applyDup(res: PblCheckResponse): void {
        this.dupCheck = res;
        this.dupById = {};
        res.results.forEach(r => this.dupById[r.id] = r);
        this.dupState = 'done';
    }

      public toggleReasoning(item: PblItem, event: Event): void {
        const target = event.target as HTMLElement;
        if (target.closest('button, select, input, a, summary')) {
          this.selectedReasoningId = null;
          return;
        }
        if (event instanceof KeyboardEvent) {
          if (event.target !== event.currentTarget) { return; }
          event.preventDefault();
        }
        this.selectedReasoningId = item.reasoning && this.selectedReasoningId !== item.id ? item.id : null;
      }

      @HostListener('document:click', ['$event.target'])
      public dismissReasoning(target: EventTarget | null): void {
        if (!(target instanceof Element) || !this.host.nativeElement.contains(target) || !target.closest('.pbl-card')) {
          this.selectedReasoningId = null;
        }
      }

      @HostListener('document:keydown.escape')
      public closeReasoning(): void {
        this.selectedReasoningId = null;
      }

      public kindOf(item: PblItem): PblKind {
        return (this.df.pblKinds[item.id] as PblKind | undefined) ?? item.kind;
      }

      public changeKind(item: PblItem, kind: PblKind): void {
        this.df.pblKinds[item.id] = kind;
        this.selectedReasoningId = null;
      }

    /** Duplicate-check result for a card, or null when it is net new / not checked. */
    public dupOf(item: PblItem): PblCheckResult | null {
        const result = this.dupById[item.id];
        return result && result.status !== 'net_new' && result.matches.length ? result : null;
    }

    public async checkDuplicates(): Promise<void> {
        if (!this.api.enabled) { return; }
        const mode = this.mode;
        this.dupState = 'checking';
        try {
            const res = await this.api.checkPblDuplicates(this.items.map(i => ({
                id: i.id,
                title: i.story.slice(0, 500),
                text: i.context.slice(0, 4000)
            })));
            if (mode !== this.mode) { return; }
            if (mode === 'examples') { this.exampleDup = res; }
            this.dupCheck = res;
            this.dupById = {};
            (res?.results ?? []).forEach(r => this.dupById[r.id] = r);
            this.dupState = res ? 'done' : 'idle';
        } catch {
            if (mode !== this.mode) { return; }
            this.dupCheck = null;
            this.dupById = {};
            this.dupState = 'error';
        }
    }

    /** Current status of a backlog item. */
    public statusOf(item: PblItem): string {
        return this.df.pbl?.[item.id] ?? 'pending';
    }

    public countOf(status: string): number {
        return this.items.filter(i => this.statusOf(i) === status).length;
    }

  /** Continue is enabled once every item has been reviewed (approved or rejected). */
  public get canContinue(): boolean {
    return this.countOf('pending') === 0;
  }

    /** Toggles approve/reject: clicking the active decision clears it back to pending. */
    public decide(item: PblItem, decision: 'approved' | 'rejected'): void {
        if (!this.df.pbl) { this.seed(); }
        const current = this.statusOf(item);
        this.df.pbl![item.id] = current === decision ? 'pending' : decision;
    }

    /** Generated backlog: drafts again from the approved documents. Examples: restores the sample backlog. */
    public regenerate(): void {
        if (this.mode === 'generated') {
            this.generate();
            return;
        }
        this.seed(true);
        this.df.toast('Example backlog restored');
        this.checkDuplicates();
    }

    public back(): void {
        this.df.go(5);
    }

    public next(): void {
        this.df.go(7);
    }

    /** Sets the example statuses; `reset` also clears decisions already made on the examples. */
    private seed(reset = false): void {
        const seeded: Record<string, string> = { ...(this.df.pbl ?? {}) };
        PBL_MOCK.forEach(i => {
            if (reset || !seeded[i.id]) { seeded[i.id] = i.status; }
            if (reset) { delete this.df.pblKinds[i.id]; }
        });
        this.df.pbl = seeded;
        this.selectedReasoningId = null;
    }
}
